import React, { useEffect, useState } from 'react';
import { Box, Grid, TextField, Button, Typography, Autocomplete, IconButton, Paper, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Chip } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { IMPROVEMENT_CHANNEL } from 'User/Application/Constants/PathsConstant';
import { useNavigate, useParams } from 'react-router-dom';
import { getDocument } from 'Document/Domain/Services/GetDocumentService';
import { IMPROVEMENT_PROPOSAL } from 'User/Application/Constants/PathsConstant';
import { getDepartmentsList } from 'Department/Domain/Services/GetDepartmentsListService';
import Loader from 'UI/Application/Components/Loaders/Loader';
import DialogLoader from 'UI/Application/Components/Loaders/DialogLoader';
import { useAuthContext } from 'Shared/Security/Domain/Contexts/authContext';
import { getUsersList } from 'User/Domain/Services/GetUsersListService';
import SearchableSelect from 'UI/Application/Components/Menus/generic/SearchableSelect';
import { getUser } from 'User/Domain/Services/GetUserService';
import { createImprovementProposal } from '../../Domain/Services/CreateImprovementProposalService';
import { getImprovementProposal } from '../../Domain/Services/GetImprovementProposalService';
import { addDocument } from 'Document/Domain/Services/AddDocumentService';

const FormImprovementProposalView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getMeFromLocalStorage } = useAuthContext();
  const [form, setForm] = useState({
    relatedProcess: '',
    improvementGoal: '',
    currentSituationDescription: '',
    relatedDepartments: [],
    trackingUserId: null,
  });
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const [successOpen, setSuccessOpen] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
    if (id !== 'new') {
      const detail = await getImprovementProposal(id);
      if (detail) {
        const ids = Array.isArray(detail.relatedDepartments) ? detail.relatedDepartments.filter(d => typeof d === 'number' || typeof d === 'string').map(Number) : [];
        setForm({
          relatedProcess: detail.relatedProcess || '',
          improvementGoal: detail.improvementGoal || '',
          currentSituationDescription: detail.currentSituationDescription || '',
          relatedDepartments: ids,
          trackingUserId: detail.trackingUserId || null,
        });
        
        if (detail.trackingUserId) {
          const u = await getUser(detail.trackingUserId);
          if (u) setUsers([u]);
        }
      }
      const docs = await getDocument(parseInt(id, 10), 'ImprovementProposal');
      setDocuments(docs || []);
    }
    // load departments for selector
      const depResp = await getDepartmentsList({ limit: 1000, offset: 0 });
      if (depResp && depResp.records) setDepartments(depResp.records);
      // cargar siempre lista de usuarios al crear para permitir asignación por cualquier usuario
      if (id === 'new') {
        const usersResp = await getUsersList({ limit: 1000, offset: 0 });
        setUsers(usersResp?.records || []);
        // Preseleccionar el usuario activo por defecto
        const me = getMeFromLocalStorage?.();
        if (me?.id) {
          setForm((prev) => ({ ...prev, trackingUserId: me.id }));
        }
      }
      setLoading(false);
    })();
  }, [id, getMeFromLocalStorage]);

  // Limpieza de URLs de preview cuando se desmonta o se eliminan docs nuevos
  useEffect(() => {
    return () => {
      documents.forEach(d => {
        if (d.documentNew && d.previewUrl) {
          try { URL.revokeObjectURL(d.previewUrl) } catch (e) { /* ignore */ }
        }
      });
    };
  }, [documents]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // Limpia error del campo y actualiza formulario
  const handleChangeAndClear = (field) => (e) => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    handleChange(e);
  };

  const handleDepartmentsChange = (_, vals) => {
    setErrors((prev) => ({ ...prev, relatedDepartments: undefined }));
    setForm({ ...form, relatedDepartments: vals.map((v) => v.id) });
  };

  const handleTrackingChange = (e) => {
    const value = e.target.value;
    setForm({ ...form, trackingUserId: value ? parseInt(value, 10) : null });
  };

  const validate = () => {
    const errs = {};
    if (!String(form.relatedProcess || '').trim()) errs.relatedProcess = 'Campo obligatorio';
    if (!String(form.improvementGoal || '').trim()) errs.improvementGoal = 'Campo obligatorio';
    if (!String(form.currentSituationDescription || '').trim()) errs.currentSituationDescription = 'Campo obligatorio';
    if (!Array.isArray(form.relatedDepartments) || form.relatedDepartments.length === 0) errs.relatedDepartments = 'Selecciona al menos un departamento';
    return errs;
  };

  const handleSave = async () => {
    if (id === 'new') {
      const errs = validate();
      if (Object.keys(errs).length) {
        setErrors(errs);
        return;
      }
      setSaving(true);
      const payload = {
        ...form,
      };
      const created = await createImprovementProposal(payload);
      if (created && created.id) {
        setCreatedId(created.id);
        // Si hay documentos seleccionados en modo new, subirlos ahora
        const newDocs = documents.filter(d => d.documentNew && d.file instanceof File);
        for (const [index, doc] of newDocs.entries()) {
          const reader = new FileReader();
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve, reject) => {
            reader.onload = async () => {
              try {
                const base64 = reader.result;
                const originalName = (doc.file && doc.file.name) ? doc.file.name : `IP-${created.id}_documento_${index}`;
                const ext = originalName && originalName.includes('.') ? originalName.split('.').pop() : 'pdf';
                await addDocument({
                  className: 'ImprovementProposal',
                  idClassName: created.id,
                  fileName: `IP-${created.id}_documento_${index}.${ext}`,
                  base64DataUrl: String(base64)
                });
                resolve();
              } catch (e) {
                reject(e);
              }
            };
            reader.onerror = reject;
            reader.readAsDataURL(doc.file);
          });
        }
        // Fin guardado: quitar spinner y mostrar popup de éxito
        setSaving(false);
        setSuccessOpen(true);
        return;
      }
      // Si no se pudo crear, quitar spinner
      setSaving(false);
      return;
    }
  // Si no es 'new', no se permite editar ni adjuntar más documentos
  return;
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    setDocuments((prev) =>
      prev.concat(
        selectedFiles.map((f) => ({
          documentNew: true,
          file: f,
          name: f.name,
          size: f.size,
          type: f.type,
          previewUrl: URL.createObjectURL(f),
        }))
      )
    );
  };

  const handleDelete = async (doc) => {
    // En creación se permiten quitar de la lista local
    if (!doc.id) {
      if (doc.previewUrl) {
        try { URL.revokeObjectURL(doc.previewUrl) } catch (e) { /* ignore */ }
      }
      setDocuments((prev) => prev.filter((d) => d !== doc));
    }
    // En detalle no se permite eliminar documentos (política no modificar)
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Loader />
    </Box>
  );

  const isNew = id === 'new';
  const isValid = Object.keys(validate()).length === 0;
  // Cualquier usuario puede asignar cuando está creando
  const canAssignTracking = isNew || (!isNew && form.trackingUserId);

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" mb={1}>
        <IconButton size="small" onClick={() => navigate(IMPROVEMENT_CHANNEL)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="body2" sx={{ ml: 1, cursor: 'pointer' }} onClick={() => navigate(IMPROVEMENT_CHANNEL)}>Volver</Typography>
      </Box>
      <Paper elevation={2} sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h6">Propuesta de mejora</Typography>
          {isNew && (
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={!isValid}>
              Guardar
            </Button>
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
        {canAssignTracking && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>Asignado a</Typography>
            <SearchableSelect
              options={users}
              value={form.trackingUserId ? String(form.trackingUserId) : ''}
              onChange={handleTrackingChange}
              disabled={!isNew}
              placeholder="Selecciona persona"
              renderOption={(u) => `${u.name ?? ''} ${u.firstSurname ?? ''} (${u.email ?? ''})`}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField 
            fullWidth 
            required
            label="Proceso relacionado"
            name="relatedProcess"
            value={form.relatedProcess}
            onChange={handleChangeAndClear('relatedProcess')}
            error={Boolean(errors.relatedProcess)}
            helperText={errors.relatedProcess}
            disabled={!isNew}
          />
        </Grid>
        <Grid item xs={12}>
          {isNew ? (
            <Autocomplete
              multiple
              options={departments}
              getOptionLabel={(o) => o && (o.name || String(o.id))}
              value={(form.relatedDepartments || []).map(id => departments.find(d => d.id === id)).filter(Boolean)}
              onChange={handleDepartmentsChange}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Departamentos relacionados" 
                  placeholder="Selecciona departamentos"
                  required
                  error={Boolean(errors.relatedDepartments)}
                  helperText={errors.relatedDepartments}
                />
              )}
              disabled={!isNew}
            />
          ) : (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Departamentos relacionados</Typography>
              {(form.relatedDepartments || []).length === 0 ? (
                <Typography color="text.secondary">Sin departamentos</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(form.relatedDepartments || []).map(id => {
                    const dep = departments.find(d => d.id === id);
                    return <Chip key={id} label={dep?.name || id} size="small" />;
                  })}
                </Box>
              )}
            </Box>
          )}
        </Grid>
        <Grid item xs={12}>
          <TextField 
            fullWidth 
            required
            label="Descripción de la situación actual" 
            name="currentSituationDescription" 
            multiline 
            rows={4} 
            value={form.currentSituationDescription} 
            onChange={handleChangeAndClear('currentSituationDescription')} 
            error={Boolean(errors.currentSituationDescription)}
            helperText={errors.currentSituationDescription}
            disabled={!isNew} 
          />
        </Grid>
        <Grid item xs={12}>
          <TextField 
            fullWidth 
            required
            label="¿Qué se pretende mejorar?" 
            name="improvementGoal" 
            value={form.improvementGoal}
            onChange={handleChangeAndClear('improvementGoal')} 
            error={Boolean(errors.improvementGoal)}
            helperText={errors.improvementGoal}
            disabled={!isNew} 
          />
        </Grid>

        {/* Adjuntos: en creación se permite adjuntar, en detalle solo visualizar/descargar */}
        <Grid item xs={12}>
          {isNew ? (
            <>
              <Button variant="outlined" startIcon={<AttachFileIcon />} component="label">
                Adjuntar medios (opcional)
                <input type="file" hidden multiple onChange={handleFileChange} />
              </Button>
              <Box mt={2}>
                {documents.map((d) => {
                  const key = d.id ?? d.previewUrl ?? `${d.name}-${d.size || ''}-${d.type || ''}`;
                  const displayName = d.fileName || d.name || (d.file && d.file.name) || 'documento';
                  return (
                    <Box key={key} display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Chip label={displayName} variant="outlined" />
                      {!d.id && (
                        <Button size="small" color="error" onClick={() => handleDelete(d)}>Quitar</Button>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </>
          ) : (
            <>
              <Typography variant="subtitle1" gutterBottom>Documentos adjuntos</Typography>
              <Box>
                {documents.length === 0 ? (
                  <Typography color="text.secondary">No hay documentos</Typography>
                ) : (
                  documents.map((d, i) => (
                    <Box key={d.id || i} display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <span>{d.fileName || d.name || `Documento ${i+1}`}</span>
                      {d.path && (
                        <Button size="small" variant="outlined" onClick={() => window.open(`/api/v1/blob-storage/${d.path}`, '_blank')}>Ver</Button>
                      )}
                    </Box>
                  ))
                )}
              </Box>
            </>
          )}
        </Grid>
        {!isNew && (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">Edición bloqueada. Para modificar, elimine y vuelva a crear la propuesta.</Typography>
          </Grid>
        )}
        </Grid>
      </Paper>

      {/* Dialogo de éxito */}
      <Dialog open={successOpen} onClose={() => setSuccessOpen(false)}>
        <DialogTitle>Propuesta guardada</DialogTitle>
        <DialogContent>
          <Typography>La propuesta se guardó correctamente.</Typography>
        </DialogContent>
        <DialogActions>
          {createdId && (
            <Button 
              variant="contained" 
              onClick={() => {
                setSuccessOpen(false);
                navigate(`${IMPROVEMENT_PROPOSAL}/${createdId}`, { replace: true });
              }}
            >
              Ver detalle
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Spinner de guardado */}
      <DialogLoader open={saving} />
    </Box>
  );
};

export default FormImprovementProposalView;


